'use client'

import { ComponentType } from 'react'

import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { ConversationMessageType } from '@marketplace-web/messaging/conversation-messaging-data'
import { FeedbackRequestMessage } from '@marketplace-web/help-center-conversation-support/automated-support-feedback-feature'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'

import StatusMessage from './StatusMessage'
import ReportSuggestion from './ReportSuggestion'
import UserMessage from './UserMessage'
import OfferRequestMessage from './OfferRequestMessage'
import OfferMessage from './OfferMessage'
import { isMessageGrouped } from '../../utils'
import usePhotosDialog from './usePhotosDialog'

const COMPONENT_MESSAGE_MAP = {
  [ConversationMessageType.ActionMessage]: StatusMessage,
  [ConversationMessageType.StatusMessage]: StatusMessage,
  [ConversationMessageType.ReportSuggestion]: ReportSuggestion,
  [ConversationMessageType.OfferMessage]: OfferMessage,
  [ConversationMessageType.OfferRequestMessage]: OfferRequestMessage,
  [ConversationMessageType.Message]: UserMessage,
  [ConversationMessageType.PortalMessage]: UserMessage,
  [ConversationMessageType.AutoReplyFeedbackRequestMessage]: FeedbackRequestMessage,
  [ConversationMessageType.ChatbotFeedbackRequestMessage]: FeedbackRequestMessage,
  [ConversationMessageType.ConversationalChatbotFeedbackRequestMessage]: FeedbackRequestMessage,
} satisfies Record<ConversationMessageType, ComponentType<any>>

type Props = {
  toggleSidebar?: () => void
  openOfferModal?: () => void
}

const ConversationMessages = ({ toggleSidebar, openOfferModal }: Props) => {
  const { conversation, conversationMessages } = useConversationContext()
  const isInlineCsatEnabled = useFeatureSwitch('launch_web_inline_csat')

  const { photosDialogElement, onThumbnailClick } = usePhotosDialog(conversationMessages)

  return (
    <>
      {conversationMessages?.map((message, index) => {
        const Component = COMPONENT_MESSAGE_MAP[message.entityType]

        if (!Component) return null

        // TODO: move the responsibility of hiding these messages to the `FeedbackRequestMessage` component
        if (
          !isInlineCsatEnabled &&
          [
            ConversationMessageType.AutoReplyFeedbackRequestMessage,
            ConversationMessageType.ConversationalChatbotFeedbackRequestMessage,
            ConversationMessageType.ChatbotFeedbackRequestMessage,
          ].includes(message.entityType)
        ) {
          return null
        }

        const key =
          message.message && 'id' in message.message ? message.message.id : message.createdTime

        return (
          <Component
            key={key}
            {...message}
            // @ts-expect-error message.message type is not properly narrowed to the type expected by each component
            message={message.message}
            isGrouped={isMessageGrouped(message, conversationMessages[index - 1])}
            isLast={index + 1 === conversationMessages.length}
            // TODO: move the responsibility of getting this prop to the `UserMessage` component
            onThumbnailClick={onThumbnailClick}
            // TODO: move the responsibility of getting this prop to the `StatusMessage` component
            toggleSidebar={toggleSidebar}
            // TODO: move the responsibility of getting this prop to the `OfferRequestMessage` component
            openOfferModal={openOfferModal}
            // TODO: move the responsibility of getting this prop to the `ReportSuggestion` component
            conversationId={conversation?.id}
            // TODO: move the responsibility of getting this prop to the `ReportSuggestion` component
            userSide={conversation?.transaction?.userSide ?? undefined}
          />
        )
      })}
      {photosDialogElement}
    </>
  )
}

export default ConversationMessages
